import { TextField } from '@material-ui/core'
import { PageFilter } from '../../../shared/src/data/pageFilter'
import { ViewType } from './viewType'
import { useCallback, useEffect, useState } from 'react'

const PAGES_REGEX = /^$|^(((\d+)(-\d+)?;)*((\d+)(-\d+)?))$/
const RANGE_REGEX = /^(\d+)-(\d+)/

type ViewTypePagesSelectorProps = {
  viewType: ViewType
  viewTypesPages: number[] | undefined
  minPage: number
  maxPage: number
  setViewTypeFilters: (viewType: ViewType, pageFilter: PageFilter) => void
  setIsStepValid: (isValid: boolean) => void
}

export default function ViewTypePagesSelector({
  viewType,
  viewTypesPages,
  minPage,
  maxPage,
  setViewTypeFilters,
  setIsStepValid,
}: ViewTypePagesSelectorProps): JSX.Element {
  const parseRangeExpression = (expression: string): number[] => {
    const result = expression?.match(RANGE_REGEX)?.slice(1).map(Number)
    if (result == null || result.length !== 2) return []

    const [pageEdge1, pageEdge2] = result
    const start = Math.min(pageEdge1, pageEdge2)
    const end = Math.max(pageEdge1, pageEdge2)
    return Array.from(new Array(end - start + 1), (x, i) => i + start)
  }

  const convertToPageFilter = useCallback(
    (pageSelectionExpression: string, viewType: string): PageFilter => {
      return {
        pageNumbers: pageSelectionExpression
          .split(';')
          .map((expression) =>
            RANGE_REGEX.test(expression)
              ? parseRangeExpression(expression)
              : Number.parseInt(expression),
          )
          .flat()
          .filter((v, i, a) => a.indexOf(v) === i),
        pageContentType: viewType,
      }
    },
    [],
  )

  const convertToRangeExpression = (numbers: number[]): string => {
    if (!numbers.length) return ''

    const sortedNumbers = Array.from(new Set(numbers)).sort((a, b) => a - b)
    const ranges = []

    let start = sortedNumbers[0]
    let end = sortedNumbers[0]
    for (let i = 1; i < sortedNumbers.length; i++) {
      if (sortedNumbers[i] === end + 1) {
        end = sortedNumbers[i]
      } else {
        ranges.push(start === end ? `${start}` : `${start}-${end}`)
        start = sortedNumbers[i]
        end = sortedNumbers[i]
      }
    }

    ranges.push(start === end ? `${start}` : `${start}-${end}`)
    return ranges.join(';')
  }

  const expressionIsValid = useCallback(
    (rangeExpression: string): boolean => {
      if (!PAGES_REGEX.test(rangeExpression)) {
        return false
      }
      const { pageNumbers } = convertToPageFilter(rangeExpression, viewType)

      return pageNumbers.every((page) => page >= minPage && page <= maxPage)
    },
    [convertToPageFilter, minPage, maxPage, viewType],
  )

  const [rangeExpression, setRangeExpression] = useState<string>(
    viewTypesPages
      ? convertToRangeExpression(viewTypesPages)
      : `${minPage}-${maxPage}`,
  )

  useEffect(() => {
    if (expressionIsValid(rangeExpression)) {
      const pageFilter = convertToPageFilter(rangeExpression, viewType)
      setViewTypeFilters(viewType, pageFilter)
      setIsStepValid(true)
    } else {
      setIsStepValid(false)
    }
  }, [
    setViewTypeFilters,
    convertToPageFilter,
    rangeExpression,
    viewType,
    expressionIsValid,
    setIsStepValid,
  ])

  return (
    <TextField
      label={viewType}
      fullWidth
      value={rangeExpression}
      color='secondary'
      error={!expressionIsValid(rangeExpression)}
      onChange={(e) => setRangeExpression(e.target.value)}
      margin='normal'
    />
  )
}
